<script setup lang="ts">
import { computed } from 'vue';
import type { FieldSchema } from '../../../types';
import { DATE_FIELDS } from '@Constants';
import { getDefaultDateFormat } from '@Helpers';
import { FIELDS_COMPONENTS } from '../../../constants';

// NOTE :: The tightest type an option can have here. `FieldEdit.options` is `any` in the module's
// own types, and the keys holding the label and the value are named by the schema at runtime
// (`edit.label` / `edit.value`), so the lookup below has to stay a dynamic one. Narrowing this to
// a fixed `{ label, value }` shape would be a lie — several schemas key their options differently.
type FieldOption = Record<string, any>;

const props = defineProps<{
  field: FieldSchema;
  model: any;
}>();

const { DATE } = FIELDS_COMPONENTS;

const isTextarea = computed(() => props.field?.settings.type === 'textarea');
const fieldValue = computed(() => {
  const { model } = props;

  const targetKeys = props.field?.render?.targetKey;
  if (targetKeys?.length && model[targetKeys[0]]) return getTargetValue(targetKeys, model);

  const isArray = Array.isArray(model);
  if (isArray) return model.length ? model.join(', ') : '-';

  const isObjectValue = model?.value;
  if (isObjectValue) return isObjectValue;

  const isUndefined = !model;
  if (isUndefined) return '-';

  const isDate = props.field.render?.component === DATE || DATE_FIELDS.includes(String(props.field.settings.type));
  if (isDate) return handleDate(model);

  if (props.field.render?.useOptionLabel) return getOptionLabel(model);

  return model;
});

// HELPERS
// NOTE :: A select stores its raw value ("d/m/Y"), so read mode prints that token unless the
// field asks for the label. Opt-in through `render.useOptionLabel` on purpose: turning it on for
// every select would silently rewrite what each details panel in the app already shows.
function getOptionLabel(model: FieldSchema['model']) {
  const options: FieldOption[] = props.field.edit?.options ?? [];
  const labelKey = props.field.edit?.label ?? 'label';
  const valueKey = props.field.edit?.value ?? 'value';

  const selectedOption = options.find((option) => option?.[valueKey] === model);

  return selectedOption?.[labelKey] ?? model;
}

function handleDate(model: any) {
  const isBirthdayField = props.field.settings.type === 'birthday';

  if (isBirthdayField && model?.birthdayModel)
    return getDefaultDateFormat(model.birthdayModel, props.field.settings?.withTime);

  if (model instanceof Date) return getDefaultDateFormat(model, props.field.settings?.withTime);

  if (props.field.settings?.withTime) return getDefaultDateFormat(model, true);

  return getDefaultDateFormat(model);
}

function getTargetValue(targetKeys: string[], targetSource: any) {
  let targetValue = targetSource;

  targetKeys.forEach((key) => (targetValue = targetValue[key]));

  return targetValue;
}
</script>

<template>
  <p :class="['whitespace-pre-line text-sm [overflow-wrap:anywhere]', { 'ml-4': isTextarea }]">{{ fieldValue }}</p>
</template>
